import './sales-transaction-summary.scss';

import { each } from 'lodash';

import type { HasFiscalPrinter } from '@pos/app/scripts/services/has-fiscal-printer';
import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { Broadcaster } from '@core/app/scripts/services/broadcaster';
import { CurrentSession } from '@shared/app/scripts/services/current-session';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { Mask } from '@core/app/scripts/services/mask';
import { Persist } from '@core/app/scripts/services/persist';
import { Route } from '@pos/app/scripts/services/route';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { FashionCheque } from '@shared/app/scripts/services/fashion-cheque';

/*
 * Component responsible for /sales-transaction-summary route.
 */
angular
  .module('posApp')
  .directive(
    'salesTransactionSummary',
    (
      $sce: ng.ISCEService,
      broadcaster: Broadcaster,
      currentSession: CurrentSession,
      displayMessage: DisplayMessage,
      hasFiscalPrinter: HasFiscalPrinter,
      isReturn,
      legacyPrint,
      mask: Mask,
      navigateTo: NavigateTo,
      persist: Persist,
      pos,
      reprintReceipt,
      retailTransactions,
      route: Route,
      salesCalculations,
      settingsManager: SettingsManager,
      translate: ng.translate.ITranslateService,
      fashionCheque: FashionCheque,
    ) => ({
      restrict: 'E',

      scope: true,
      template: require('./sales-transaction-summary.html').default,

      link: function ($scope, element, attrs) {
        const $translate = translate;

        displayMessage.showLoading();

        $scope.param = route.param;
        $scope.ref = route.ref;
        $scope.$sce = $sce;

        $scope.pos = pos;
        $scope.hasFiscalPrinter = hasFiscalPrinter;
        $scope.vat = settingsManager.settings.vat;
        $scope.displayDiscountReason =
          settingsManager.settings.displayAndPrintDiscountReason;

        $scope.showExtraArticleInfo = false;

        function resetSelectReceiptDate() {
          persist.remove('selectreceipt.date');
        }

        $scope.showExtraArticleInfo =
          settingsManager.settings.pos?.extendedReceipt;

        $scope.transaction = {};
        $scope.openEmailPopup = false;

        function showTransaction(transaction) {
          displayMessage.hideLoading();
          $scope.transaction = angular.copy(transaction);
          salesCalculations.preCalc($scope.transaction);
        }

        retailTransactions.filter({ _id: $scope.param }).then((response) => {
          if (response && response.length) {
            showTransaction(response[0]);
          }
        });

        $scope.getConsignmentStatusText = () => {
          let status = '';
          if (angular.isObject($scope.transaction)) {
            if ($scope.transaction.status === 'open') {
              status = $translate.instant('OpenConsignment');
            } else if ($scope.transaction.status === 'closed') {
              status = $translate.instant('ClosedConsignment');
            }
          }

          return status;
        };

        $scope.openConsignmentTransaction = () => {
          let linkTransaction = { _id: $scope.transaction.link.id };
          pos.selectReceipt(linkTransaction, 'pos.route#select-receipt');
          retailTransactions
            .filter({ _id: $scope.transaction.link.id })
            .then((response) => {
              if (response && response.length) {
                showTransaction(response[0]);
              }
            });
        };

        $scope.getConsignmentClass = () => {
          if ($scope.transaction && $scope.transaction.status === 'open') {
            return { green: true };
          } else if (
            $scope.transaction &&
            $scope.transaction.status === 'closed'
          ) {
            return { red: true };
          }
        };

        $scope.isTransactionClosed = () => {
          return $scope.transaction && $scope.transaction.status === 'closed';
        };

        $scope.isConsignment = () => {
          return $scope.transaction && $scope.transaction.type === 9;
        };

        $scope.getDate = (transaction) => {
          return transaction?.printed
            ? transaction.printed
            : transaction?.created?.date;
        };

        $scope.toggleEmailPopup = () => {
          if (!settingsManager.settings.email.active) {
            displayMessage.message({
              message: $translate.instant('MissingEmailingModule'),
              type: 'info',
            });
            return;
          }

          broadcaster.send('setFocus', ['receiptEmail', true]);
          $scope.openEmailPopup = $scope.openEmailPopup ? false : true;
        };

        /*
         * Send the receipt re-print as an email
         */
        $scope.emailReceipt = () => {
          $scope.openEmailPopup = false;

          if ($scope.transaction.receipt_email) {
            legacyPrint
              .emailReceipt(
                $scope.transaction._id,
                $scope.transaction.receipt_email,
              )
              .then(() => {
                resetSelectReceiptDate();

                navigateTo('pos.route#select-receipt');
              });
          } else {
            displayMessage.message($translate.instant('MissingEmailAddress'));
          }
        };

        // print the current transaction
        $scope.printReceipt = () => {
          displayMessage.showLoading();

          reprintReceipt($scope.transaction).then(angular.noop, () => {
            mask.hide();
            displayMessage.message({
              message: $translate.instant('PrintingNotPossible'),
              time: 4000,
              type: 'alert',
            });
          });

          // assume the printout is succesfull
          resetSelectReceiptDate();

          $scope.navigateBack();
        };

        $scope.hasGiftReceipt = () => {
          return currentSession.countryCode === 'ES';
        };

        $scope.printGiftReceipt = () => {
          const transaction = angular.copy($scope.transaction);

          delete transaction.payments;
          delete transaction.vat;

          delete transaction.finalTotalDiscount;
          delete transaction.totalNumber;
          delete transaction.totalReturn;
          delete transaction.total;
          delete transaction.payments;
          delete transaction.difference;
          delete transaction.totalPaid;
          delete transaction.totalAmount;

          each(transaction.receipt, (row) => {
            row.price = '';
            row.lineTotal = '';
            row.nettPrice = '';
          });

          return legacyPrint.receiptRaw(transaction);
        };

        $scope.selectCurrentReceipt = () => {
          if ($scope.transaction.nr) {
            pos.loadTransaction($scope.transaction.nr);
            resetSelectReceiptDate();
            navigateTo('pos.route#pos');
          }
        };

        $scope.navigateBack = () => {
          mask.hide();
          if ($scope.ref) {
            navigateTo($scope.ref);
          } else {
            navigateTo('pos.route#pos');
          }
        };

        $scope.isReturnDisabled = () => {
          return (
            hasFiscalPrinter() &&
            (!$scope.transaction.fiscal_receipt_nr ||
              isReturn($scope.transaction))
          );
        };

        $scope.isFashionChequeCoupon = function (item) {
          return item.couponNr ? fashionCheque.validateCode(item.couponNr) : '';
        };

        $scope.getMaskedCouponNr = function (item) {
          return item.couponNr ? item.couponNr.replace(/\d{4}$/, '****') : '';
        };
      },
    }),
  );
